import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FiArrowLeft,
  FiClock,
  FiUsers,
  FiDollarSign,
  FiMapPin,
  FiTruck,
  FiPhone,
  FiMail,
  FiCalendar,
  FiShoppingCart,
  FiHeart,
  FiShare2,
  FiChefHat,
  FiCoffee,
  FiImage,
} from "react-icons/fi";
import { mealPlansAPI } from "../../services/api";
import LoadingSpinner from "../../components/LoadingSpinner";
import toast from "react-hot-toast";

const Container = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem 0;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #666;
  font-size: 1rem;
  cursor: pointer;
  margin-bottom: 2rem;
  padding: 0.5rem 0;
  transition: color 0.2s;

  &:hover {
    color: #2d5016;
  }
`;

const MealPlanCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 2rem;
`;

const MealPlanImage = styled.div`
  position: relative;
  height: 400px;
  background: linear-gradient(135deg, #2d5016, #4a7c59);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .placeholder {
    font-size: 4rem;
    opacity: 0.7;
  }
`;

const MealPlanContent = styled.div`
  padding: 2rem;
`;

const MealPlanHeader = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #2d5016;
  margin-bottom: 1rem;
`;

const Description = styled.p`
  font-size: 1.1rem;
  color: #666;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const VendorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 12px;
`;

const VendorAvatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2d5016, #4a7c59);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  font-weight: bold;
`;

const VendorDetails = styled.div`
  flex: 1;
`;

const VendorName = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: #2d5016;
  margin-bottom: 0.25rem;
`;

const VendorCuisine = styled.p`
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
`;

const MealPlanStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border-left: 4px solid #2d5016;
`;

const StatIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: linear-gradient(135deg, #2d5016, #4a7c59);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d5016;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  color: #666;
  font-size: 0.9rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &.primary {
    background: linear-gradient(135deg, #2d5016, #4a7c59);
    color: white;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(45, 80, 22, 0.3);
    }
  }

  &.secondary {
    background: white;
    color: #2d5016;
    border: 2px solid #2d5016;

    &:hover {
      background: #2d5016;
      color: white;
    }
  }

  &.outline {
    background: white;
    color: #666;
    border: 2px solid #e0e0e0;

    &:hover {
      border-color: #2d5016;
      color: #2d5016;
    }
  }
`;

const MealItemsSection = styled.div`
  margin-top: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #2d5016;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MealItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const MealItemCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
`;

const MealItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const MealName = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: #2d5016;
  margin-bottom: 0.5rem;
`;

const DayOfWeek = styled.span`
  background: #f0f8f0;
  color: #2d5016;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const MealDescription = styled.p`
  color: #666;
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 1rem;
`;

const MealNutrition = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
  font-size: 0.8rem;
  color: #666;
`;

const NutritionItem = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #dc3545;
`;

const MealPlanDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    data: mealPlanData,
    isLoading,
    error,
  } = useQuery(["mealPlan", id], () => mealPlansAPI.getMealPlan(id), {
    enabled: !!id,
  });

  const handlePurchase = () => {
    navigate(`/checkout/${id}`);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: mealPlanData?.data?.mealPlan?.title,
        text: mealPlanData?.data?.mealPlan?.description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard!");
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <Container>
        <Content>
          <LoadingSpinner />
        </Content>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Content>
          <ErrorMessage>
            <h2>Error loading meal plan</h2>
            <p>{error.message || "Failed to load meal plan"}</p>
            <Button onClick={handleBack} className="primary">
              <FiArrowLeft />
              Go Back
            </Button>
          </ErrorMessage>
        </Content>
      </Container>
    );
  }

  // Backend returns data.data.mealPlan (raw DB object) and data.data.items
  const mealPlanRaw = mealPlanData?.data?.data?.mealPlan;
  const itemsRaw = mealPlanData?.data?.data?.items || [];

  if (!mealPlanRaw) {
    return (
      <Container>
        <Content>
          <ErrorMessage>
            <h2>Meal plan not found</h2>
            <p>The meal plan you're looking for doesn't exist.</p>
            <Button onClick={handleBack} className="primary">
              <FiArrowLeft />
              Go Back
            </Button>
          </ErrorMessage>
        </Content>
      </Container>
    );
  }

  // Use the raw data directly (it's already in the correct format from backend)
  const mealPlan = mealPlanRaw;
  const items = itemsRaw;

  return (
    <Container>
      <Content>
        <BackButton onClick={handleBack}>
          <FiArrowLeft />
          Back to Meal Plans
        </BackButton>

        <MealPlanCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <MealPlanImage>
            {(mealPlan.imageUrl || mealPlan.image_url) ? (
              <img 
                src={mealPlan.imageUrl || mealPlan.image_url} 
                alt={mealPlan.title || 'Meal Plan'} 
                onError={(e) => {
                  // Hide broken image and show placeholder
                  if (e.target && e.target.parentNode) {
                    e.target.style.display = 'none';
                    const placeholder = e.target.parentNode.querySelector('.placeholder');
                    if (placeholder) {
                      placeholder.style.display = 'flex';
                    }
                  }
                }}
              />
            ) : null}
            <div 
              className="placeholder" 
              style={{ 
                display: (mealPlan.imageUrl || mealPlan.image_url) ? 'none' : 'flex' 
              }}
            >
              <FiImage />
            </div>
          </MealPlanImage>

          <MealPlanContent>
            <MealPlanHeader>
              <Title>{mealPlan.title}</Title>
              <Description>{mealPlan.description}</Description>

              <VendorInfo>
                <VendorAvatar>
                  {mealPlan.business_name?.charAt(0) || "V"}
                </VendorAvatar>
                <VendorDetails>
                  <VendorName>{mealPlan.business_name}</VendorName>
                  <VendorCuisine>{mealPlan.cuisine_type} Cuisine</VendorCuisine>
                </VendorDetails>
              </VendorInfo>
            </MealPlanHeader>

            <MealPlanStats>
              {mealPlan.offers_delivery && (
                <StatCard>
                  <StatIcon>
                    <FiTruck />
                  </StatIcon>
                  <StatContent>
                    <StatValue>
                      $
                      {Math.round(
                        parseFloat(mealPlan.base_price) +
                          parseFloat(mealPlan.delivery_fee || 0)
                      ).toLocaleString()}
                    </StatValue>
                    <StatLabel>Delivery</StatLabel>
                    {mealPlan.delivery_fee > 0 && (
                      <div
                        style={{
                          fontSize: "0.75rem",
                          color: "#666",
                          marginTop: "0.25rem",
                        }}
                      >
                        Base: $
                        {Math.round(
                          parseFloat(mealPlan.base_price)
                        ).toLocaleString()}{" "}
                        + Delivery: $
                        {Math.round(
                          parseFloat(mealPlan.delivery_fee)
                        ).toLocaleString()}
                      </div>
                    )}
                  </StatContent>
                </StatCard>
              )}

              {mealPlan.offers_pickup && (
                <StatCard>
                  <StatIcon>
                    <FiMapPin />
                  </StatIcon>
                  <StatContent>
                    <StatValue>
                      $
                      {Math.round(
                        parseFloat(mealPlan.base_price)
                      ).toLocaleString()}
                    </StatValue>
                    <StatLabel>Pickup</StatLabel>
                    <div
                      style={{
                        fontSize: "0.75rem",
                        color: "#666",
                        marginTop: "0.25rem",
                      }}
                    >
                      {mealPlan.pickup_address || "Vendor location"}
                    </div>
                  </StatContent>
                </StatCard>
              )}

              {!mealPlan.offers_delivery && !mealPlan.offers_pickup && (
                <StatCard>
                  <StatIcon>
                    <FiDollarSign />
                  </StatIcon>
                  <StatContent>
                    <StatValue>
                      ${Math.round(parseFloat(mealPlan.price)).toLocaleString()}
                    </StatValue>
                    <StatLabel>Per {mealPlan.plan_type}</StatLabel>
                  </StatContent>
                </StatCard>
              )}

              <StatCard>
                <StatIcon>
                  <FiClock />
                </StatIcon>
                <StatContent>
                  <StatValue>
                    {mealPlan.durationDays || mealPlan.duration_days || "N/A"} {mealPlan.durationDays || mealPlan.duration_days ? "days" : ""}
                  </StatValue>
                  <StatLabel>Duration</StatLabel>
                </StatContent>
              </StatCard>

              <StatCard>
                <StatIcon>
                  <FiMapPin />
                </StatIcon>
                <StatContent>
                  <StatValue>{mealPlan.delivery_radius} km</StatValue>
                  <StatLabel>Delivery Radius</StatLabel>
                </StatContent>
              </StatCard>
            </MealPlanStats>

            <ActionButtons>
              <Button className="primary" onClick={handlePurchase}>
                <FiShoppingCart />
                Purchase Now
              </Button>
              <Button className="secondary" onClick={handleShare}>
                <FiShare2 />
                Share
              </Button>
              <Button className="outline">
                <FiHeart />
                Save
              </Button>
            </ActionButtons>

            {items.length > 0 && (
              <MealItemsSection>
                <SectionTitle>
                  <FiCoffee />
                  Meal Plan Items
                </SectionTitle>
                <MealItemsGrid>
                  {items.map((item, index) => (
                    <MealItemCard key={item.id || index}>
                      <MealItemHeader>
                        <div>
                          <MealName>{item.meal_name}</MealName>
                        </div>
                        <DayOfWeek>{item.day_of_week}</DayOfWeek>
                      </MealItemHeader>
                      <MealDescription>{item.description}</MealDescription>
                      <MealNutrition>
                        <NutritionItem>
                          <span>Calories:</span>
                          <span>{item.calories || "N/A"}</span>
                        </NutritionItem>
                        <NutritionItem>
                          <span>Protein:</span>
                          <span>{item.protein || "N/A"}</span>
                        </NutritionItem>
                        <NutritionItem>
                          <span>Carbs:</span>
                          <span>{item.carbs || "N/A"}</span>
                        </NutritionItem>
                        <NutritionItem>
                          <span>Fat:</span>
                          <span>{item.fat || "N/A"}</span>
                        </NutritionItem>
                      </MealNutrition>
                    </MealItemCard>
                  ))}
                </MealItemsGrid>
              </MealItemsSection>
            )}
          </MealPlanContent>
        </MealPlanCard>
      </Content>
    </Container>
  );
};

export default MealPlanDetail;
